import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { List } from 'immutable';
import { isArray, uniq } from 'lodash';
import { concat, Observable, of } from 'rxjs';
import { catchError, flatMap, map, switchMap, take, tap } from 'rxjs/operators';
import { ActionsEnum as AppActionsEnum } from '../../store/app.actions';
import { ActionsEnum as AuthActionsEnum } from '../auth-data/auth-data.actions';
import { AuthDataStore } from '../auth-data/auth-data.store';
import { BrowserManagerDataDispatcher } from '../browser-manager-data/browser-manager-data.dispatcher';
import { LocalStorageService } from '../browser-manager-data/local-storage.service';
import {
  ActionsEnum,
  CompleteMigration,
  completeMigrationError,
  completeMigrationSuccess,
  FetchAccountMigration,
  fetchAccountMigration,
  FetchAccountMigrationError,
  fetchAccountMigrationError,
  markOnboardingAsCompleted,
  SetCompletedOnboardingData,
  setCompletedOnboardingData,
  UpdateAccountMigration,
  updateAccountMigration,
} from './onboarding-data.actions';
import { OnboardingDataService } from './onboarding-data.service';
import {
  I2G_COMPLETED_ONBOARDING_KEY,
  NEW_USER_COOKIE,
  OnboardingDataStore,
} from './onboarding-data.store';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { Settings } from '../settings-data/settings.model';
import { fetchSettings } from '../settings-data/settings-data.actions';
import { fetchAccountDetails } from '../account-data/account-data.actions';
import {
  fetchFeaturesData,
  fetchQuotasBannersCanvases,
} from '../features-data/features-data.actions';
import { environment } from '../../../environments/environment';

@Injectable()
export class OnboardingDataEffects {
  constructor(
    private _actions: Actions,
    private _authDataStore: AuthDataStore,
    private _onboardingDataStore: OnboardingDataStore,
    private _settingsDataStore: SettingsDataStore,
    private _onboardingDataService: OnboardingDataService,
    private _localStorageService: LocalStorageService,
    private _browserManagerDataDispatcher: BrowserManagerDataDispatcher
  ) {}

  @Effect()
  getAccountMigration: Observable<
    UpdateAccountMigration | FetchAccountMigrationError
  > = this._actions.pipe(
    ofType(ActionsEnum.FETCH_ACCOUNT_MIGRATION),
    switchMap(() =>
      this._onboardingDataService.getAccountMigrationInfo().pipe(
        map(updateAccountMigration),
        catchError(() => of(fetchAccountMigrationError()))
      )
    )
  );

  @Effect()
  onInit: Observable<
    FetchAccountMigration | SetCompletedOnboardingData
  > = this._actions.pipe(
    ofType(AppActionsEnum.INIT_APP),
    take(1),
    map(() => {
      let completedOnboardingData: string[] = JSON.parse(
        this._localStorageService.getItem(I2G_COMPLETED_ONBOARDING_KEY)
      );

      if (!isArray(completedOnboardingData)) {
        completedOnboardingData = [];
      }

      return setCompletedOnboardingData(List(completedOnboardingData));
    })
  );

  @Effect()
  onAuth: Observable<FetchAccountMigration> = this._actions.pipe(
    ofType(AuthActionsEnum.AUTHENTICATED),
    take(1),
    map(() => fetchAccountMigration())
  );

  @Effect({ dispatch: false })
  setCompletedOnboardingData: Observable<any> = this._actions.pipe(
    ofType(ActionsEnum.SET_COMPLETED_ONBOARDING_DATA),
    tap(({ payload }: SetCompletedOnboardingData) => {
      this._browserManagerDataDispatcher.setLocalStorageItem(
        I2G_COMPLETED_ONBOARDING_KEY,
        JSON.stringify(uniq(payload.accountUserIds.toArray()))
      );
    })
  );

  @Effect()
  markOnboardingAsCompleted: Observable<SetCompletedOnboardingData> = this._actions.pipe(
    ofType(ActionsEnum.MARK_ONBOARDING_AS_COMPLETED),
    flatMap(() => this._authDataStore.getUserId()),
    flatMap((userId: string) =>
      this._onboardingDataStore.getCompletedOnboardingData().pipe(
        map((completedOnboardingData: List<string>) => {
          this._browserManagerDataDispatcher.removeCookies(
            [NEW_USER_COOKIE],
            '/',
            environment.baseDomain
          );

          return setCompletedOnboardingData(
            completedOnboardingData.push(userId)
          );
        })
      )
    )
  );

  @Effect()
  completeMigration: Observable<any> = this._actions.pipe(
    ofType(ActionsEnum.COMPLETE_MIGRATION),
    switchMap(({ payload }: CompleteMigration) => {
      const { dispatchId } = payload;

      return this._settingsDataStore.getSettings().pipe(
        flatMap((settings: Settings) =>
          this._onboardingDataService.completeMigration(settings)
        ),
        flatMap(() => {
          return concat([
            fetchSettings(dispatchId),
            fetchAccountMigration(),
            fetchAccountDetails(dispatchId),
            fetchFeaturesData(dispatchId),
            fetchQuotasBannersCanvases(),
            markOnboardingAsCompleted(),
            completeMigrationSuccess(dispatchId),
          ]);
        }),
        catchError((error) => of(completeMigrationError(dispatchId, error)))
      );
    })
  );
}
